import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { HERO_MESSAGE } from "@/data/revamp";

const EASE = [0.76, 0, 0.24, 1];

const lineVariants = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 0.95, ease: EASE, delay: 0.15 + i * 0.08 },
  }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=90&w=2200";

export const Hero = () => {
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 800], [0, 160]);
  const parallaxScale = useTransform(scrollY, [0, 800], [1.05, 1.18]);

  useEffect(() => {
    document.documentElement.style.setProperty("--hero-loaded", "1");
  }, []);

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden bg-[var(--bg)]">
      {/* Parallax hero image behind a warm cream veil */}
      <motion.div
        style={{ y: parallaxY, scale: parallaxScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Orphion hero"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
      </motion.div>
      {/* Cream veil so the light theme text reads clearly */}
      <div className="absolute inset-0 bg-[rgb(var(--bg-rgb)/0.78)] mix-blend-lighten" />
      <div className="absolute inset-0 bg-gradient-to-b from-[rgb(var(--bg-rgb)/0.6)] via-[rgb(var(--bg-rgb)/0.3)] to-[rgb(var(--bg-rgb))]" />

      {/* Grid guides — hidden on mobile where they add noise */}
      <div className="absolute inset-0 pointer-events-none hidden md:block">
        <div className="max-w-[1440px] mx-auto h-full px-6 md:px-10 grid grid-cols-12 gap-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="h-full border-r border-[var(--ink)]/[0.04] last:border-r-0"
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10 pt-28 md:pt-40 pb-16 md:pb-24 min-h-[100svh] flex flex-col justify-between">
        <div />

        <div>
          <h1 className="font-display text-[clamp(2.25rem,9vw,8rem)] leading-[0.95] md:leading-[0.9] tracking-[-0.03em] text-[var(--ink)]">
            <span className="line-mask">
              <motion.span
                variants={lineVariants}
                initial="hidden"
                animate="show"
                custom={0}
                className="block"
              >
                {HERO_MESSAGE.titleA}
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span
                variants={lineVariants}
                initial="hidden"
                animate="show"
                custom={1}
                className="block"
              >
                <span className="font-serif-accent italic text-[var(--accent)]">
                  {HERO_MESSAGE.titleAccent}
                </span>{" "}
                {HERO_MESSAGE.titleB}
              </motion.span>
            </span>
          </h1>

          <div className="mt-8 md:mt-14 grid md:grid-cols-12 gap-6 md:gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
              className="md:col-span-6 font-body text-base md:text-xl text-[var(--ink-80)] max-w-xl leading-relaxed"
            >
              {HERO_MESSAGE.support}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
              className="md:col-span-6 flex flex-wrap gap-4 md:justify-end"
            >
              <Link to={HERO_MESSAGE.ctaPrimary.to} className="btn-pill" data-testid={HERO.ctaPrimary}>
                <span>{HERO_MESSAGE.ctaPrimary.label}</span>
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.6} />
              </Link>
              <Link to={HERO_MESSAGE.ctaSecondary.to} className="btn-ghost" data-testid={HERO.ctaSecondary}>
                <span>{HERO_MESSAGE.ctaSecondary.label}</span>
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="hidden md:flex items-center gap-3 text-[var(--ink-50)]"
          data-testid={HERO.scrollCue}
        >
          <ArrowDown className="w-4 h-4 animate-bounce" />
          <span className="text-xs uppercase tracking-[0.28em]">
            Scroll · GCC &amp; AI-native IT, hire-ready in 90 days
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
